import { io } from "socket.io-client";
import { waitForInit, getAuthToken } from "../graphql/client.js";
import { jwtDecode } from "jwt-decode";

let chatSocket;
let joinedRoomId = null;
// Last-fetched chat room list, kept alive across ChatList mount/unmount (e.g.
// closing and reopening the chat panel) so reopening it renders the previous
// list instantly instead of a fresh skeleton + round trip every time.
let cachedRooms = null;

export const getCachedRooms = () => cachedRooms;
export const setCachedRooms = (rooms) => { cachedRooms = rooms; };

export const connectChatSocket = (id, type) => {
    // Return existing socket whether connected or still connecting —
    // prevents two simultaneous callers from creating duplicate sockets.
    if (chatSocket) {
        return chatSocket;
    }

    chatSocket = io(import.meta.env.VITE_CHAT_SOCKET_URL, {
        query: { id, type },
        // The server now verifies this token (signed, same one the
        // GraphQL/REST API already requires) and derives who id/type
        // actually are from it - id/type above are no longer trusted for
        // auth, only used for the pre-handshake-response UX. Matches
        // lib/socket.js's existing auth: { token } pattern in this repo.
        auth: { token: getAuthToken() ?? undefined },
        // Polling-only - the reverse proxy in front of some deployments of
        // this widget doesn't support the WebSocket upgrade, so leaving
        // "websocket" here makes the client stall on a failed upgrade
        // handshake before falling back, which made chat appear stuck
        // loading. Matches the merchant repo's identical fix in
        // lib/chatSocket.js.
        transports: ["polling"],
    });

    chatSocket.on("connect", () => {
        if (joinedRoomId) {
            chatSocket.emit("join_room", { roomId: joinedRoomId });
        }
    });

    chatSocket.on("disconnect", () => {});

    return chatSocket;
};

// Pre-connect as soon as the auth token is available — before any React component mounts.
// This eliminates the WebSocket handshake delay when the user navigates to chat.
waitForInit.then(() => {
    const token = getAuthToken();
    if (!token) return;
    try {
        const decoded = jwtDecode(token);
        const userId = Number(decoded?.userId);
        if (userId) connectChatSocket(userId, "USER");
    } catch { }
});

export const joinChatRoom = (roomId) => {
    joinedRoomId = roomId;
    if (chatSocket?.connected) {
        chatSocket.emit("join_room", { roomId });
    }
};

export const getChatSocket = () => chatSocket;

export const disconnectChatSocket = () => {
    if (chatSocket) {
        chatSocket.removeAllListeners();
        chatSocket.disconnect();
        chatSocket = null;
    }
    joinedRoomId = null;
    // Logging out must not leak the previous user's chat list into the next
    // session's first render.
    cachedRooms = null;
};
